import { api } from './index';

export interface RFQ {
  id: string;
  rfqNumber: string;
  requisitionId?: string;
  title: string;
  description: string;
  category: 'GOODS' | 'SERVICES';
  templateId?: string;
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'SENT' | 'QUOTES_RECEIVED' | 'UNDER_REVIEW' | 'AWARDED' | 'CANCELLED' | 'EXPIRED';
  deadline: string;
  currency: 'MVR' | 'USD';
  awardedQuotationId?: string;
  purchaseOrderId?: string;
  createdBy: {
    id: string;
    fullName: string;
    email: string;
  };
  items: RFQItem[];
  vendors: RFQVendorMapping[];
  quotations: Quotation[];
  communications?: RFQCommunicationLog[];
  vendorCategories?: Array<{
    id: string;
    categoryId: string;
    category: {
      id: string;
      name: string;
    };
  }>;
  vendorSubcategories?: Array<{
    id: string;
    subcategoryId: string;
    subcategory: {
      id: string;
      name: string;
    };
  }>;
  selectedVendorIds?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface RFQItem {
  id: string;
  itemName: string;
  description?: string;
  quantity: number;
  unit: string;
  specifications?: Record<string, any>;
  order: number;
}

export interface RFQVendorMapping {
  id: string;
  vendorId: string;
  vendor: {
    id: string;
    businessName: string;
    email: string;
  };
  selectionReason?: string;
  invitationMethod: 'EMAIL' | 'PORTAL' | 'BOTH';
  status: 'INVITED' | 'RESPONDED' | 'DECLINED' | 'NO_RESPONSE';
  quotationSubmitted: boolean;
}

export interface Quotation {
  id: string;
  quotationNumber: string;
  rfqId: string;
  vendorId: string;
  vendor: {
    id: string;
    businessName: string;
    email: string;
  };
  status: 'DRAFT' | 'SUBMITTED' | 'ACCEPTED' | 'REJECTED';
  totalPrice: number;
  currency: 'MVR' | 'USD';
  validityDays: number;
  deliveryDays: number;
  paymentTerms?: string;
  notes?: string;
  items: QuotationItem[];
  submittedAt?: string;
}

export interface QuotationItem {
  id: string;
  rfqItemId: string;
  rfqItem?: RFQItem;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
  description?: string;
}

export interface RFQCommunicationLog {
  id: string;
  type: 'INVITATION' | 'REMINDER' | 'CLARIFICATION' | 'RESPONSE';
  method: 'EMAIL' | 'PORTAL';
  subject: string;
  body: string;
  sentAt: string;
  openedAt?: string;
  readAt?: string;
  vendor?: {
    id: string;
    businessName: string;
    email: string;
  };
}

export interface CreateRFQDto {
  title: string;
  description: string;
  category: 'GOODS' | 'SERVICES';
  templateId?: string;
  deadline: string;
  currency?: 'MVR' | 'USD';
  items: Omit<RFQItem, 'id'>[];
  vendorCategoryIds?: string[]; // Vendor categories to target
  vendorSubcategoryIds?: string[]; // Vendor subcategories to target
  selectedVendorIds?: string[]; // Directly selected vendor IDs
}

export interface SendRFQDto {
  vendorIds?: string[]; // Optional - will auto-select from categories if not provided
  method: 'EMAIL' | 'PORTAL' | 'BOTH';
}

export interface AwardRFQDto {
  quotationId: string;
  notes: string;
}

export interface VendorSelectionRules {
  categoryMatch?: boolean;
  pastPerformanceThreshold?: number;
  priceRangeMin?: number;
  priceRangeMax?: number;
  limit?: number;
}

export const rfqApi = {
  list: (filters?: { status?: RFQ['status']; category?: RFQ['category']; search?: string }): Promise<RFQ[]> =>
    api.get('/rfq', { params: filters }).then((res) => res.data),

  get: (id: string): Promise<RFQ> =>
    api.get(`/rfq/${id}`).then((res) => res.data),

  create: (data: CreateRFQDto): Promise<RFQ> =>
    api.post('/rfq', data).then((res) => res.data),

  createFromTemplate: (data: { templateId: string; title: string; description: string; deadline: string; currency?: 'MVR' | 'USD'; items?: Omit<RFQItem, 'id'>[] }): Promise<RFQ> =>
    api.post('/rfq/from-template', data).then((res) => res.data),

  update: (id: string, data: Partial<CreateRFQDto>): Promise<RFQ> =>
    api.patch(`/rfq/${id}`, data).then((res) => res.data),

  send: (id: string, data: SendRFQDto): Promise<RFQ> =>
    api.post(`/rfq/${id}/send`, data).then((res) => res.data),

  award: (id: string, data: AwardRFQDto): Promise<RFQ> =>
    api.post(`/rfq/${id}/award`, data).then((res) => res.data),

  cancel: (id: string, reason: string): Promise<RFQ> =>
    api.post(`/rfq/${id}/cancel`, { reason }).then((res) => res.data),

  selectVendors: (id: string, rules: VendorSelectionRules): Promise<Array<{ vendorId: string; reason: string; score: number }>> =>
    api.post(`/rfq/${id}/select-vendors`, rules).then((res) => res.data),

  getRecommendedVendors: (id: string, limit?: number): Promise<Array<{ vendorId: string; reason: string; score: number }>> =>
    api.get(`/rfq/${id}/recommended-vendors`, { params: { limit } }).then((res) => res.data),

  getComparison: (id: string): Promise<any> =>
    api.get(`/rfq/${id}/comparison`).then((res) => res.data),

  sendInvitation: (id: string, data: SendRFQDto): Promise<{ message: string }> =>
    api.post(`/rfq/${id}/send-invitation`, data).then((res) => res.data),

  sendReminder: (id: string, vendorIds?: string[]): Promise<{ message: string }> =>
    api.post(`/rfq/${id}/send-reminder`, { vendorIds }).then((res) => res.data),

  getCommunications: (id: string): Promise<RFQCommunicationLog[]> =>
    api.get(`/rfq/${id}/communications`).then((res) => res.data),

  submitForApproval: (id: string): Promise<RFQ> =>
    api.post(`/rfq/${id}/submit-approval`).then((res) => res.data),

  approve: (id: string, comments?: string): Promise<RFQ> =>
    api.post(`/rfq/${id}/approve`, { comments }).then((res) => res.data),

  reject: (id: string, comments: string): Promise<RFQ> =>
    api.post(`/rfq/${id}/reject`, { comments }).then((res) => res.data),

  getVendorRFQs: (): Promise<RFQ[]> =>
    api.get('/rfq/vendor/my-rfqs', { useVendorAuth: true }).then((res) => res.data),

  getVendorRFQ: (id: string): Promise<RFQ> =>
    api.get(`/rfq/vendor/${id}`, { useVendorAuth: true }).then((res) => res.data),
};

